import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@heroui/react';
import { TermStudent } from '@/entities/Courses';
import { formatDate, formatDateTime } from '@/shared/lib/utils/formatDate';
import { formatPercentOrDash } from '@/shared/lib/utils/formatPercent';
import { IndeterminateCheckbox } from '@/shared/ui/IndeterminateCheckbox';
import { Svg } from '@/shared/ui/Svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';
import OpenedLockIcon from '@/shared/assets/icons/opened-lock.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';

export const studentsColumns = (): ColumnDef<TermStudent>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        uniqueKey="all-students-select"
        name="all-students"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        uniqueKey={`student-select-${row.original.id}`}
        name="student"
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    size: 64,
    minSize: 64,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>ID</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'id',
    size: 96,
    minSize: 96,
    cell: ({ row }) => <span className="text-black-headline">{row.original.id}</span>,
  },
  {
    header: 'User',
    accessorFn: ({ firstName, lastName }) => `${firstName} ${lastName}`,
    size: 240,
    cell: ({ row }) => (
      <div className="flex w-0 min-w-full flex-col">
        <span className="break-words font-medium text-black-headline">
          {row.original.firstName} {row.original.lastName}
        </span>
        <span className="break-all text-sm text-grey-secondary">{row.original.email}</span>
      </div>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Progress</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'progressByLessons',
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5 whitespace-nowrap">
        <span className="text-black-headline">
          {formatPercentOrDash(row.original.progressByLessons)} lessons
        </span>
        <span className="text-grey-secondary text-xs">
          {formatPercentOrDash(row.original.progressByModules)} modules
        </span>
      </div>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Attendance</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'attendance',
    cell: ({ row }) => (
      <span className="text-black-headline">{formatPercentOrDash(row.original.attendance)}</span>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Last Activity</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'lastActivity',
    cell: ({ row }) => {
      const lastActivity = formatDateTime(
        row.original.lastActivity || row.original.registrationDate,
      );
      return (
        <div className="flex flex-col">
          <span className="text-black-headline">{lastActivity?.date}</span>
          <span className="text-grey-secondary text-sm">{lastActivity?.time}</span>
        </div>
      );
    },
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Registration Date</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'registrationDate',
    cell: ({ row }) => (
      <span className="text-black-headline">{formatDate(row.original.registrationDate)}</span>
    ),
  },
  {
    id: 'actions',
    cell: ({ row, table }) => {
      const meta = table.options.meta as
        | {
            onBlock?: (student: TermStudent) => void;
            onDelete?: (student: TermStudent) => void;
          }
        | undefined;
      return (
        <div className="flex items-center gap-2">
          {meta?.onBlock && (
            <Button
              isIconOnly
              variant="light"
              size="sm"
              className="text-red-tertiary"
              onPress={() => meta.onBlock?.(row.original)}
              aria-label="Block student"
            >
              <Svg Icon={OpenedLockIcon} width={20} height={20} />
            </Button>
          )}
          {meta?.onDelete && (
            <Button
              isIconOnly
              variant="light"
              size="sm"
              className="text-red-tertiary"
              onPress={() => meta.onDelete?.(row.original)}
              aria-label="Delete student"
            >
              <Svg Icon={TrashIcon} width={20} height={20} />
            </Button>
          )}
        </div>
      );
    },
  },
];
